/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, ReactNode, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PositionService, PositionDto } from '@/api/portal'
import { useOrders } from '@/contexts/useOrders'
import { TRACKING_ACTIVE_STATUSES } from '@/pages/Orders/config/order-status.config'

const ACTIVE_STATUS_SET = new Set(TRACKING_ACTIVE_STATUSES)

export interface DriverPositionsContextValue {
    positions: PositionDto[]
    positionsByDdoId: Map<string, PositionDto>
    isLoading: boolean
}

export const DriverPositionsContext = createContext<DriverPositionsContextValue | undefined>(undefined)

interface DriverPositionsProviderProps {
    children: ReactNode
}

export const DriverPositionsProvider = ({ children }: DriverPositionsProviderProps) => {
    const { orders, lastUpdated } = useOrders()

    // Only request positions for orders with active statuses (visible on the map)
    const ddoIds = useMemo(() => {
        const ids = orders
            .filter(order => order.status && ACTIVE_STATUS_SET.has(order.status))
            .map(order => order.id)
            .filter(Boolean) as string[]
        return ids.sort()
    }, [orders])

    // Use a stable query key based on sorted ddoIds
    const { data: positions = [], isLoading } = useQuery({
        queryKey: ['driverPositions', ddoIds, lastUpdated],
        queryFn: async () => {
            if (ddoIds.length === 0) return []
            return await PositionService.positionControllerSearch({
                ddoIds,
            })
        },
        enabled: ddoIds.length > 0,
        refetchOnMount: true,
    })

    // Map positions by ddoId (one position per order)
    const positionsByDdoId = useMemo(() => {
        const map = new Map<string, PositionDto>()
        positions.forEach(position => {
            map.set(position.ddoId, position)
        })
        return map
    }, [positions])

    const value = useMemo<DriverPositionsContextValue>(
        () => ({
            positions,
            positionsByDdoId,
            isLoading,
        }),
        [positions, positionsByDdoId, isLoading],
    )

    return <DriverPositionsContext.Provider value={value}>{children}</DriverPositionsContext.Provider>
}

export const useDriverPositions = () => {
    const context = useContext(DriverPositionsContext)
    if (!context) {
        throw new Error('useDriverPositions must be used within a DriverPositionsProvider')
    }
    return context
}
